import { adresseDe } from './consignes'
import type { SourcePiece } from './domaine'
import { nomExpediteur } from './groupes'
import { FOURNISSEURS_PORTAIL, type FournisseurPortail } from './portails'

// Fournisseurs et voies d'arrivée de leurs pièces (demande de Pierre-Marie du 20/10/2026) :
// comment les pièces de chaque fournisseur entrent dans l'appli, d'après celles reçues. Un
// connecteur (collecteur d'un portail), la pièce jointe d'un mail, le mail qui vaut pièce, une
// plateforme de facturation, ou à la main (assistant, dépôt, photo) : ce dernier cas signale un
// fournisseur à couvrir par un connecteur ou une consigne de tri. Les comptes viennent de la base.
// Une pièce sans fiche fournisseur compte pour l'adresse qui l'a envoyée, sinon pour son
// connecteur : beaucoup de reçus arrivent par mail sans que l'émetteur soit reconnu.

/** Voies d'arrivée, de la plus automatique à la plus manuelle (ordre des égalités). */
export const VOIES_PIECE = [
  'connecteur',
  'mail_vaut_piece',
  'mail',
  'plateforme',
  'assistant',
  'photo',
  'depot',
] as const
export type VoiePiece = (typeof VOIES_PIECE)[number]

/** Voies où un humain (ou Claude à sa demande) a apporté la pièce. */
const A_LA_MAIN: ReadonlySet<VoiePiece> = new Set(['assistant', 'photo', 'depot'])

/** La voie d'une pièce d'après sa source ; null pour une version remplie, qui n'arrive de nulle part. */
export function voieDePiece(p: {
  source: SourcePiece
  rendueDepuisMail: boolean
}): VoiePiece | null {
  switch (p.source) {
    case 'portail':
      return 'connecteur'
    case 'gmail':
      return p.rendueDepuisMail ? 'mail_vaut_piece' : 'mail'
    case 'mcp':
      return 'assistant'
    case 'depot':
    case 'photo':
    case 'plateforme':
      return p.source
    case 'remplissage':
      return null
  }
}

/** Un compte de pièces d'un émetteur, par source, connecteur, boîte et entité. */
export type ComptePieces = {
  /** Fiche du fournisseur émetteur ; null : émetteur non reconnu (ou non visible). */
  tiersId: string | null
  tiers: string | null
  /** En-tête « Nom <adresse> » du mail, pour une pièce sans fiche. */
  expediteur: string | null
  source: SourcePiece
  rendueDepuisMail: boolean
  /** Clé du connecteur (FOURNISSEURS_PORTAIL) qui a relevé ces pièces. */
  connecteur: string | null
  /** Adresse de la boîte d'où viennent ces pièces. */
  boite: string | null
  entite: string | null
  nombre: number
  /** Date de la plus récente (AAAA-MM-JJ). */
  derniere: string | null
}

export type FournisseurVoies = {
  /** Clé stable de la ligne : fiche, adresse d'expéditeur, connecteur, ou « sans ». */
  cle: string
  tiersId: string | null
  nom: string
  /** Adresse qui envoie les pièces, quand le fournisseur n'a pas de fiche. */
  adresse: string | null
  total: number
  principale: VoiePiece
  /** Nombre de pièces par voie, la plus fréquente d'abord. */
  voies: { voie: VoiePiece; nombre: number }[]
  connecteurs: string[]
  boites: string[]
  entites: string[]
  derniere: string | null
  /** La voie principale est manuelle : un connecteur ou une consigne manque peut-être. */
  aLaMain: boolean
}

const parRang = (a: VoiePiece, b: VoiePiece) => VOIES_PIECE.indexOf(a) - VOIES_PIECE.indexOf(b)
const trier = (s: Set<string>) => [...s].sort((a, b) => a.localeCompare(b, 'fr'))

export const SANS_EMETTEUR = 'Émetteur non reconnu'

/** Nom court d'un connecteur (« Amazon »), d'après le libellé de FOURNISSEURS_PORTAIL. */
export function nomDuConnecteur(cle: string): string {
  const fiche = Object.hasOwn(FOURNISSEURS_PORTAIL, cle)
    ? FOURNISSEURS_PORTAIL[cle as FournisseurPortail]
    : null
  return fiche ? (fiche.libelle.split(' (')[0] ?? fiche.libelle) : cle
}

/** À qui compter une ligne : la fiche, sinon l'adresse d'expéditeur, sinon le connecteur. */
function emetteurDe(l: ComptePieces): { cle: string; nom: string; adresse: string | null } {
  if (l.tiersId) return { cle: `tiers:${l.tiersId}`, nom: l.tiers ?? SANS_EMETTEUR, adresse: null }
  const adresse = adresseDe(l.expediteur)
  if (adresse)
    return { cle: `mail:${adresse}`, nom: nomExpediteur(l.expediteur) ?? adresse, adresse }
  if (l.connecteur)
    return { cle: `connecteur:${l.connecteur}`, nom: nomDuConnecteur(l.connecteur), adresse: null }
  return { cle: 'sans', nom: SANS_EMETTEUR, adresse: null }
}

type Cumul = {
  cle: string
  tiersId: string | null
  nom: string
  adresse: string | null
  voies: Map<VoiePiece, number>
  connecteurs: Set<string>
  boites: Set<string>
  entites: Set<string>
  derniere: string | null
}

const totalDe = (c: Cumul) => [...c.voies.values()].reduce((s, n) => s + n, 0)

/** Un nom comparable : sans accents, casse ni ponctuation. */
const nomComparable = (t: string) =>
  t
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

/**
 * Un même fournisseur sous plusieurs lignes (fiches en double, pièces sans fiche, raisons
 * sociales d'un même connecteur) : réunies quand elles partagent un nom comparable, le leur ou
 * celui du connecteur qui les a relevées. Le groupe prend le nom de sa plus grosse ligne, ou
 * celui du connecteur quand ses noms diffèrent ; les émetteurs non reconnus restent à part.
 */
function reunir(cumuls: readonly Cumul[]): Cumul[] {
  const parent = cumuls.map((_, i) => i)
  const racine = (i: number): number => {
    while (parent[i] !== i) i = parent[i] = parent[parent[i]!]!
    return i
  }
  const parNom = new Map<string, number>()
  cumuls.forEach((c, i) => {
    if (c.cle === 'sans') return
    for (const n of [c.nom, ...[...c.connecteurs].map(nomDuConnecteur)].map(nomComparable)) {
      if (!n) continue
      const j = parNom.get(n)
      if (j === undefined) parNom.set(n, i)
      else parent[racine(i)] = racine(j)
    }
  })
  const groupes = new Map<number, Cumul[]>()
  cumuls.forEach((c, i) => groupes.set(racine(i), [...(groupes.get(racine(i)) ?? []), c]))
  return [...groupes.values()].map((membres) => {
    if (membres.length === 1) return membres[0]!
    const tries = [...membres].sort((a, b) => totalDe(b) - totalDe(a))
    const tete = tries[0]!
    const voies = new Map<VoiePiece, number>()
    for (const m of tries) for (const [v, n] of m.voies) voies.set(v, (voies.get(v) ?? 0) + n)
    const union = (k: 'connecteurs' | 'boites' | 'entites') =>
      new Set(tries.flatMap((m) => [...m[k]]))
    const connecteurs = union('connecteurs')
    const noms = new Set(tries.map((m) => nomComparable(m.nom)))
    const connecteur = [...tete.connecteurs, ...connecteurs][0]
    const fiche = tries.find((m) => m.tiersId)
    return {
      cle: tete.cle,
      tiersId: fiche?.tiersId ?? null,
      nom: noms.size > 1 && connecteur ? nomDuConnecteur(connecteur) : tete.nom,
      adresse: fiche ? null : tete.adresse,
      voies,
      connecteurs,
      boites: union('boites'),
      entites: union('entites'),
      derniere: tries.reduce<string | null>(
        (d, m) => (m.derniere && (!d || m.derniere > d) ? m.derniere : d),
        null,
      ),
    }
  })
}

/** Les fournisseurs, du plus au moins de pièces, chacun avec ses voies d'arrivée. */
export function voiesDesFournisseurs(lignes: readonly ComptePieces[]): FournisseurVoies[] {
  const parEmetteur = new Map<string, Cumul>()
  for (const l of lignes) {
    const voie = voieDePiece(l)
    if (!voie || l.nombre <= 0) continue
    const e = emetteurDe(l)
    let c = parEmetteur.get(e.cle)
    if (!c) {
      c = {
        cle: e.cle,
        tiersId: l.tiersId,
        nom: e.nom,
        adresse: e.adresse,
        voies: new Map(),
        connecteurs: new Set(),
        boites: new Set(),
        entites: new Set(),
        derniere: null,
      }
      parEmetteur.set(e.cle, c)
    }
    c.voies.set(voie, (c.voies.get(voie) ?? 0) + l.nombre)
    if (voie === 'connecteur' && l.connecteur) c.connecteurs.add(l.connecteur)
    if ((voie === 'mail' || voie === 'mail_vaut_piece') && l.boite) c.boites.add(l.boite)
    if (l.entite) c.entites.add(l.entite)
    if (l.derniere && (!c.derniere || l.derniere > c.derniere)) c.derniere = l.derniere
  }

  return reunir([...parEmetteur.values()])
    .map((c) => {
      const voies = [...c.voies]
        .map(([voie, nombre]) => ({ voie, nombre }))
        .sort((a, b) => b.nombre - a.nombre || parRang(a.voie, b.voie))
      const principale = voies[0]!.voie
      return {
        cle: c.cle,
        tiersId: c.tiersId,
        nom: c.nom,
        adresse: c.adresse,
        total: voies.reduce((s, v) => s + v.nombre, 0),
        principale,
        voies,
        connecteurs: trier(c.connecteurs),
        boites: trier(c.boites),
        entites: trier(c.entites),
        derniere: c.derniere,
        aLaMain: A_LA_MAIN.has(principale),
      }
    })
    .sort((a, b) => b.total - a.total || a.nom.localeCompare(b.nom, 'fr'))
}
